import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedStation } from "../../../store/orm/userSession";
import { useKeydownListener } from "../../../hooks/useKeydownListener";
import ORMfunctions from "../../../hooks/app/general";
import { AddressSearch } from "./AddressSearch";
import { RootState } from "../../../store/store";
import { getStationById } from "../../../store/orm/stations";

import { Station } from "../../../store/orm/stations";

interface InputSearchProps {
  type: "start" | "end";
  useapi?: boolean;
  regioncodes?: string[];
  rangelimit?: number;
  resultlimit?: number;
  viewType?: string;
  readonly?: boolean;
  labelId?: string;
  enabledstations?: string;
}

export const InputSearch: React.FC<InputSearchProps> = ({
  type,
  useapi = false,
  regioncodes = ["DE"],
  rangelimit = 50,
  resultlimit = 10,
  viewType = "",
  readonly = false,
  labelId,
  enabledstations = null,
}) => {
  const dispatch = useDispatch();

  const triggerRef = useRef<HTMLDivElement>(null);

  let stations = [] as Station[];

  const [showSelect, setShowSelect] = useState(false);
  const [showSelectEnd, setShowSelectEnd] = useState(false);
  const [readonlyReact, setReadonlyReact] = useState(readonly);

  // Get station data from Redux store
  let startStation = useSelector(
    (state: RootState) => state.userSession?.selectedStation,
  );

  const endStation = useSelector(
    (state: RootState) => state.userSession?.selectedStationEnd,
  );

  if (!enabledstations) {
    stations = useSelector(
      (state: RootState) => state.stations.stations,
    ) as Station[];
  } else {
    const allstations = useSelector(
      (state: RootState) => state.stations.stations,
    ) as Station[];

    stations = allstations.filter((item) => enabledstations.includes(item.id));

    if (!stations.find((element) => element.id === startStation)) {
      // startStation = stations[0].id;
      if (stations[0])
        dispatch(
          setSelectedStation({ type: "start", stationId: stations[0].id }),
        );
    }
  }

  const stationSelected = type === "start" ? startStation! : endStation!;

  const stationSelectedObject = useSelector((state: RootState) =>
    getStationById(state, stationSelected),
  );

  useEffect(() => {
    if (stations.length <= 1) {
      setReadonlyReact(true);
    } else {
      setReadonlyReact(false);
    }
  }, [stations]);

  useEffect(() => {
    if (startStation === endStation) {
      setShowSelectEnd(false);
    } else {
      setShowSelectEnd(!!stationSelected);
    }
  }, [startStation, endStation, stationSelected]);

  // Handle opening the select
  const handleOpen = useCallback(
    (event: React.MouseEvent | KeyboardEvent) => {
      if (readonlyReact) return;

      if (event.type === "keydown") {
        const kbEvent = event as KeyboardEvent;
        switch (kbEvent.key) {
          case "Enter":
          case "ArrowUp":
          case "ArrowDown":
            if (!showSelect) {
              setShowSelect(true);
            }
            if (type === "end") {
              setShowSelectEnd(true);
            }
            kbEvent.preventDefault();
            break;
          case "Escape":
            setShowSelect(false);
            break;
          case "Tab":
          case "Shift":
            break;
          default:
            kbEvent.preventDefault();
            break;
        }
      } else {
        setShowSelect(true);
      }
    },
    [readonlyReact, showSelect, type],
  );

  // Handle closing the select
  const handleClose = useCallback(() => {
    setShowSelect(false);
  }, []);

  // Handle station selection
  const handleStationSelect = useCallback(
    (stationId: string) => {
      // console.log("setstation", stationId);

      if (!stationId) return;

      if (stationId === "startend") {
        setShowSelect(false);
        setShowSelectEnd(false);
        if (type === "end") {
          dispatch(setSelectedStation({ type: "end", station: null }));
        }
        return;
      }

      setShowSelect(false);

      if (stationSelected === stationId) return;

      // const station = stations.find((s) => s.id === stationId);
      // // console.log(stationId);

      if (type === "start") {
        dispatch(setSelectedStation({ type: "start", stationId }));
      } else {
        if (startStation! === stationId) {
          dispatch(setSelectedStation({ type: "end", stationId: null }));
          setShowSelectEnd(false);
          return;
        } else {
          dispatch(setSelectedStation({ type: "end", stationId }));
          setShowSelectEnd(true);
        }
      }
    },
    [dispatch, type, stationSelected, startStation, stations],
  );

  // Keyboard listener for trigger element
  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      handleOpen(event);
    },
    [handleOpen],
  );

  useKeydownListener(triggerRef, handleKeydown);

  // Render AddressSearch when open
  if (showSelect) {
    return (
      <AddressSearch
        stationSelected={stationSelected}
        stationSelectedObject={stationSelectedObject}
        stations={stations}
        rangelimit={rangelimit}
        resultlimit={resultlimit}
        regionCodes={regioncodes}
        useapi={useapi}
        type={type}
        labelId={labelId}
        onSelect={handleStationSelect}
        onCloseEvent={handleClose}
      />
    );
  }

  // Render unselected end station
  if (type === "end" && !showSelectEnd) {
    return (
      <div
        className={`orm-stationselect__endstation__unselected ${
          viewType === "mapModule" ? "orm-lmap" : ""
        }`}
        id="stationselect-trigger"
        aria-expanded={showSelect}
        aria-controls="stationselect-listbox"
        aria-activedescendant={
          stationSelected ? `station-${stationSelected}` : ""
        }
        aria-labelledby={labelId}
        role="combobox"
        aria-autocomplete="list"
        tabIndex={readonlyReact ? -1 : 0}
        onClick={handleOpen}
        ref={triggerRef}
      >
        <div className="orm-fakecheck"></div>
        <p className="orm-stationselect__label">
          {ORMfunctions.i18n("general/returnStation") || "Rückgabestation"}
        </p>
      </div>
    );
  }

  // Render selected station
  if (stationSelectedObject) {
    return (
      <div
        className={`orm-stationselect text-left ${readonlyReact ? "orm-stationselect--disabled" : ""}`}
      >
        <div
          className="orm-stationselect__select"
          id="stationselect-trigger"
          aria-expanded={showSelect}
          aria-controls="stationselect-listbox"
          aria-activedescendant={
            stationSelected ? `station-${stationSelected}` : ""
          }
          aria-labelledby={labelId}
          role="combobox"
          aria-autocomplete="list"
          tabIndex={readonlyReact ? -1 : 0}
          onClick={handleOpen}
          ref={triggerRef}
          title={`${stationSelectedObject.name} - ${stationSelectedObject.street}, ${stationSelectedObject.city}`}
        >
          <div>
            <strong>{stationSelectedObject.name}</strong>
          </div>
          <small className="noBreak overflowHidden textTrim">
            {stationSelectedObject.street}, {stationSelectedObject.city}
          </small>
          <span className="orm-station__address__icon">
            <i className="fas fa-map-marker-alt"></i>
          </span>
        </div>
      </div>
    );
  }

  // Render placeholder
  return (
    <div className="orm-stationselect text-left">
      <div
        className="orm-stationselect__select-left"
        onClick={handleOpen}
        role="combobox"
        aria-autocomplete="list"
        tabIndex={readonlyReact ? -1 : 0}
        aria-expanded={showSelect}
        aria-controls="stationselect-listbox"
        ref={triggerRef}
      >
        <span>
          {ORMfunctions.i18n("general/chooseStation") || "Station wählen"}
        </span>
        <span className="orm-station__address__icon--center">
          <i className="fas fa-map-marker-alt"></i>
        </span>
      </div>
    </div>
  );
};
